import { PerspectiveCamera, Quaternion, Vector3 } from 'three';
import { BALANCE } from '../config/balance';
import {
  clamp,
  degToRad,
  easeInOutCubic,
  rightFromForwardUp,
  signedAngleAround,
  smootherstep,
  wrapAngle,
} from '../utils/math';
import { headRotation, headWorldPosition, muzzleWorldPosition } from '../graphics/robotRig';
import type { Robot } from '../player/Robot';
import type { CollisionWorld } from '../world/CollisionWorld';
import { FirstPersonCamera } from './FirstPersonCamera';
import { ThirdPersonCamera } from './ThirdPersonCamera';
import { createCameraTransform, type CameraTransform } from './CameraTransform';

export type CameraMode = 'third' | 'first';

const scratchRight = new Vector3();
const scratchUp = new Vector3();
const scratchAim = new Vector3();
const scratchQuat = new Quaternion();
/** The viewer offset scaled for the current camera mode. */
const scratchViewerOffset = new Vector3();
/** Basis scratch for the visible head rig's viewer offset. */
const scratchRigRight = new Vector3();
const scratchRigUp = new Vector3();
const scratchRigForward = new Vector3();
const scratchRigInverse = new Quaternion();
/** Re-expressing the look in a new frame: a right axis in that frame. */
const scratchHoldRight = new Vector3();
const scratchSteerAxis = new Vector3();

/**
 * The vertical field of view for a viewport aspect.
 *
 * Three.js builds the frustum from a vertical angle, so a portrait window
 * (aspect well below one) would squeeze the horizontal view into a tunnel.
 * Below square, the vertical angle grows until the horizontal view is as wide
 * as the setting gives in a square window; `applyFov` still clamps the result
 * to the maximum. Landscape is untouched, so the shipped feel is unchanged.
 */
export function fovForAspect(fov: number, aspect: number): number {
  if (!(aspect > 0) || aspect >= 1) return fov;
  const half = degToRad(fov) / 2;
  return (2 * Math.atan(Math.tan(half) / aspect) * 180) / Math.PI;
}

/**
 * Owns the two viewpoints and the transition between them.
 *
 * The rig keeps a single "view yaw" that is relative to the robot's body, so
 * switching viewpoint preserves the direction the player was looking in.
 *
 * Head tracking is a view-position layer: it never rotates the aim. The mouse
 * aims, the head tracker translates the first-person viewer, and the projection
 * matrix becomes an off-axis window.
 */
export class CameraRig {
  readonly camera: PerspectiveCamera;

  mode: CameraMode = 'third';

  private relativeYaw = 0;
  private relativePitch = 0;
  /** The body's heading the view yaw was last reconciled against. */
  private lastHeading = 0;
  /** The surface the last heading was measured in. */
  private lastSurfaceId: string | null = null;
  /** The body's forward the view yaw was last reconciled against. */
  private readonly lastForward = new Vector3(0, 0, -1);
  /**
   * The body's up, smoothed while the body is in free flight.
   *
   * Outside free flight it is the body's up exactly, because the 90 degree
   * roll at a corner is the signature of the game. In mid air the body is
   * free to turn, and the camera follows it gently rather than spinning the
   * world with it.
   */
  private readonly smoothedUp = new Vector3(0, 1, 0);
  /**
   * The plane the view is composed in.
   *
   * Normally the body's up, so a corner's roll is automatic. After a sideways
   * step it is pinned to the plane the player stepped *off*: the picture stays
   * level while the body walks the new wall, and the view swings onto the new
   * plane only when the player asks for it (a head tilt, or `Q`). The yaw and
   * pitch are built on this vector too, so the mouse stays screen-relative
   * whatever plane the picture is holding.
   */
  private readonly viewUp = new Vector3(0, 1, 0);
  /** The up of the surface the player stepped off, while the view is pinned. */
  private readonly pinnedUp = new Vector3(0, 1, 0);
  /** The forward the look yaw is measured from in the pinned plane. */
  private readonly pinnedForward = new Vector3(0, 0, -1);
  /**
   * The forward the look yaw is measured from this frame.
   *
   * The body's forward normally, so the view is body-relative as it always
   * was. While the view is pinned it is the pinned plane's forward, which makes
   * the whole look frame a fixed world plane: the body can turn and blend
   * underneath without the aim moving at all.
   */
  private readonly lookReference = new Vector3(0, 0, -1);
  /** True while the view is held on the plane the player stepped off. */
  private pinnedPlane = false;
  /** Seconds into the swing from the pinned plane onto the body's, if running. */
  private swivelElapsed = Number.POSITIVE_INFINITY;
  /** The body's transition count the pin was last reconciled against. */
  private lastTransitionCount = 0;
  /** The pin-and-swivel model; the game turns it off with a setting. */
  private planeSwivel = true;
  /** The field of view the player asked for, without the rotation kick. */
  private baseFov: number = BALANCE.camera.defaultFov;
  /** The viewport aspect the projection is built for. */
  private aspect = 1;
  /** Viewer offset from the head-tracking pipeline, in camera-local metres. */
  private readonly viewOffset = new Vector3();
  /** Player setting for the third-person arm slide; 1 is the balance default. */
  private thirdPersonParallax = 1;
  /**
   * Distance to the virtual screen, metres. The balance default (arm's length)
   * until the player calibrates their real viewing distance.
   */
  private windowDistance: number = BALANCE.camera.offAxis.screenDistance;
  /**
   * How far the viewer may travel, metres. The shipped clamps by default;
   * rebuilt from the screen calibration so the window clamp keeps the same
   * feel once the offset is in real metres.
   */
  private readonly viewerReach: { x: number; y: number; forward: number } = {
    x: BALANCE.camera.offAxis.maxViewerX,
    y: BALANCE.camera.offAxis.maxViewerY,
    forward: BALANCE.camera.offAxis.maxViewerForward,
  };
  /**
   * Flip the lateral head-tracked offset. On by default: the leaned-the-other-
   * way shift is what reads as a window into the room.
   */
  private invertHeadTracking = false;
  /**
   * The bullet-time lean lock: a look correction that re-aims the camera onto
   * the target the lean would otherwise slide off (see `combat/Aim.leanLock`).
   * Zero the rest of the time.
   */
  private leanLockYaw = 0;
  private leanLockPitch = 0;
  /**
   * The look yaw, wrapped or clamped so it never accumulates past the point
   * where the player can find the front again.
   */
  get viewYaw(): number {
    return this.relativeYaw;
  }

  private readonly thirdPerson: ThirdPersonCamera;
  private readonly firstPerson: FirstPersonCamera;
  private readonly fromTransform: CameraTransform = createCameraTransform();
  private readonly targetTransform: CameraTransform = createCameraTransform();
  private blendElapsed = Number.POSITIVE_INFINITY;
  /** Smooth look move used when bullet time turns the camera toward a threat. */
  private lookEaseElapsed = Number.POSITIVE_INFINITY;
  private lookEaseDuration = 0;
  private lookFromYaw = 0;
  private lookFromPitch = 0;
  private lookToYaw = 0;
  private lookToPitch = 0;
  private recoilPitch = 0;
  private shakeStrength = 0;
  private shakeTime = 0;
  private readonly baseForward = new Vector3();
  private readonly viewForward = new Vector3();
  private readonly viewRight = new Vector3();

  constructor(collision: CollisionWorld, fov: number = BALANCE.camera.defaultFov) {
    this.camera = new PerspectiveCamera(fov, 1, BALANCE.camera.firstPerson.near, 500);
    this.thirdPerson = new ThirdPersonCamera(collision);
    this.firstPerson = new FirstPersonCamera(collision);
  }

  /** The raw head-tracking viewer offset, in camera-local metres. */
  get headViewOffset(): Vector3 {
    return this.viewOffset;
  }

  /**
   * The head's aim offset relative to the body, in radians.
   *
   * The mouse owns this in both modes; the head tracker never touches it. The
   * turret can face the full circle, so the yaw wraps rather than clamps and
   * the pod and gun can point behind the body.
   */
  get headAimYaw(): number {
    return wrapAngle(this.relativeYaw);
  }

  get headAimPitch(): number {
    const limit = (BALANCE.camera.head.maxPitch * Math.PI) / 180;
    return clamp(this.relativePitch + this.recoilPitch, -limit, limit);
  }

  get headAimRoll(): number {
    return 0;
  }

  /** The recoil view kick currently applied to the first person camera. */
  get recoilAmount(): number {
    return this.recoilPitch;
  }

  setFov(fov: number): void {
    if (Math.abs(this.baseFov - fov) < 0.01) return;
    this.baseFov = fov;
    this.applyFov(0);
  }

  /**
   * Resize the projection for a viewport aspect.
   *
   * Kept on the rig rather than set on the camera directly so the portrait
   * field-of-view compensation is applied in one place.
   */
  setAspect(aspect: number): void {
    const safe = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    if (Math.abs(this.aspect - safe) < 1e-6) return;
    this.aspect = safe;
    this.camera.aspect = safe;
    this.camera.updateProjectionMatrix();
    this.applyFov(0);
  }

  /** The viewport aspect the projection is built for. */
  get viewAspect(): number {
    return this.aspect;
  }

  /** Apply the base field of view plus the rotation kick. */
  private applyFov(kick: number): void {
    const base = fovForAspect(this.baseFov, this.aspect);
    const fov = clamp(base + kick, BALANCE.camera.minFov, BALANCE.camera.maxFov);
    if (Math.abs(this.camera.fov - fov) < 0.001) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  /** Apply accumulated mouse movement. */
  applyLook(deltaX: number, deltaY: number, sensitivity: number, invertY: boolean): void {
    // A frame with no movement must not touch the look at all. The game applies
    // the mouse every frame, and `setLook` cancels a scripted ease, so routing a
    // zero through here killed the bullet-time turn to the attacker before it
    // could move the eye.
    if (deltaX === 0 && deltaY === 0) return;
    this.cancelLookEase();
    const scale = BALANCE.camera.lookSensitivity * sensitivity;
    this.setLook(
      this.relativeYaw + deltaX * scale,
      this.relativePitch + deltaY * scale * (invertY ? 1 : -1),
    );
  }

  /** Set the look angles directly, for scripted sessions and tests. */
  setLook(yaw: number, pitch: number): void {
    this.cancelLookEase();
    // Both modes can turn fully around; wrap rather than clamp so the value
    // cannot grow until the player cannot find the front again.
    this.relativeYaw = wrapAngle(yaw);
    this.relativePitch = clamp(
      pitch,
      (BALANCE.camera.firstPerson.minPitch * Math.PI) / 180,
      (BALANCE.camera.firstPerson.maxPitch * Math.PI) / 180,
    );
  }

  /**
   * Turn the view toward a threat over a short blend instead of snapping.
   * Any real mouse movement cancels the blend immediately.
   */
  easeLook(yaw: number, pitch: number, duration = 0.18): void {
    this.lookFromYaw = this.relativeYaw;
    this.lookFromPitch = this.relativePitch;
    this.lookToYaw = yaw;
    this.lookToPitch = clamp(
      pitch,
      (BALANCE.camera.firstPerson.minPitch * Math.PI) / 180,
      (BALANCE.camera.firstPerson.maxPitch * Math.PI) / 180,
    );
    this.lookEaseDuration = Math.max(0, duration);
    this.lookEaseElapsed = 0;
    if (this.lookEaseDuration === 0) this.finishLookEase();
  }

  private cancelLookEase(): void {
    this.lookEaseElapsed = Number.POSITIVE_INFINITY;
    this.lookEaseDuration = 0;
  }

  private finishLookEase(): void {
    this.relativeYaw = wrapAngle(this.lookToYaw);
    this.relativePitch = this.lookToPitch;
    this.cancelLookEase();
  }

  private updateLookEase(dt: number): void {
    if (this.lookEaseElapsed >= this.lookEaseDuration) return;
    this.lookEaseElapsed += dt;
    const t = smootherstep(this.lookEaseElapsed / this.lookEaseDuration);
    const yawDelta = wrapAngle(this.lookToYaw - this.lookFromYaw);
    this.relativeYaw = wrapAngle(this.lookFromYaw + yawDelta * t);
    this.relativePitch = this.lookFromPitch + (this.lookToPitch - this.lookFromPitch) * t;
    if (this.lookEaseElapsed >= this.lookEaseDuration) this.finishLookEase();
  }

  /**
   * Apply the head-tracking viewer offset, in camera-local metres. `forward`
   * is positive toward the virtual screen.
   *
   * This is a viewpoint translation and nothing else: it moves the eye (and,
   * in first person, the head rig carrying the gun) and shifts the frustum to
   * match. It never touches the aim, so leaning can never swing the shot.
   */
  applyViewOffset(x: number, y: number, forward: number): void {
    const reach = this.viewerReach;
    // `0 - x` rather than `-x`, so a zero offset stays `+0` (tests compare it).
    const lateral = this.invertHeadTracking ? 0 - x : x;
    this.viewOffset.set(
      clamp(lateral, -reach.x, reach.x),
      clamp(y, -reach.y, reach.y),
      clamp(forward, -reach.forward, reach.forward),
    );
  }

  /** Flip or unflip the lateral head-tracked offset, from the setting. */
  setInvertHeadTracking(invert: boolean): void {
    this.invertHeadTracking = invert;
  }

  /**
   * Set the lean-lock correction, radians, applied to the look. The game feeds
   * this while the bullet-time window is open and zeroes it when it closes.
   */
  setLeanLock(yaw: number, pitch: number): void {
    this.leanLockYaw = yaw;
    this.leanLockPitch = pitch;
  }

  clearHeadTracking(): void {
    this.applyViewOffset(0, 0, 0);
  }

  /**
   * How much of the viewer offset the current camera mode applies.
   *
   * First person is the reference window, so it uses the offset as calibrated.
   * Third person applies its own multiplier: the arm holds the camera several
   * metres behind the robot, where the lean reads differently from arm's
   * length. The camera translation and the frustum shift always use the same
   * scaled value, which is what keeps the window anchored and turns the shift
   * into parallax instead of a pan.
   *
   * The player's third-person parallax setting multiplies the balance default,
   * so the slider ranges from no arm slide to well past the old 2x.
   */
  private get parallaxScale(): number {
    if (this.mode !== 'third') return 1;
    return BALANCE.camera.offAxis.thirdPersonParallaxScale * this.thirdPersonParallax;
  }

  /** Set the player's third-person arm-slide multiplier (0 disables it). */
  setThirdPersonParallax(scale: number): void {
    this.thirdPersonParallax = Math.max(0, scale);
  }

  /**
   * Set the distance to the virtual screen, metres, from the screen
   * calibration. Clamped to the closest the window may get.
   */
  setWindowDistance(distance: number): void {
    this.windowDistance = Math.max(BALANCE.camera.offAxis.minScreenDistance, distance);
  }

  /** Set how far the viewer may travel, metres, from the screen calibration. */
  setViewerReach(x: number, y: number, forward: number): void {
    this.viewerReach.x = Math.max(0.05, x);
    this.viewerReach.y = Math.max(0.05, y);
    this.viewerReach.forward = Math.max(0.05, forward);
  }

  /** Copy the scaled viewer offset into `out` and return it. */
  private scaledViewOffset(out: Vector3): Vector3 {
    return out.copy(this.viewOffset).multiplyScalar(this.parallaxScale);
  }

  toggleMode(): CameraMode {
    this.fromTransform.position.copy(this.camera.position);
    this.fromTransform.quaternion.copy(this.camera.quaternion);
    this.mode = this.mode === 'third' ? 'first' : 'third';
    this.blendElapsed = 0;
    return this.mode;
  }

  setMode(mode: CameraMode, immediate = false): void {
    if (this.mode === mode) return;
    if (immediate) {
      this.mode = mode;
      this.blendElapsed = Number.POSITIVE_INFINITY;
      return;
    }
    this.toggleMode();
  }

  /** Recoil view kick, in radians of weapon recoil. */
  setRecoil(amount: number): void {
    this.recoilPitch = clamp(
      amount * BALANCE.combat.recoilViewScale,
      0,
      BALANCE.combat.maxRecoilViewKick,
    );
  }

  /** A short camera kick, used for landings. */
  addShake(strength: number): void {
    this.shakeStrength = Math.min(0.4, this.shakeStrength + strength);
  }

  /**
   * Turn the pin-and-swivel model on or off.
   *
   * Off restores the automatic roll: the picture follows the body through a
   * sideways step, exactly as it did before the swivel existed.
   */
  setPlaneSwivel(enabled: boolean): void {
    if (this.planeSwivel === enabled) return;
    this.planeSwivel = enabled;
    if (!enabled) this.releasePin();
  }

  /** True while the view is held on the plane the player stepped off. */
  get isPlanePinned(): boolean {
    return this.pinnedPlane;
  }

  /** The up the camera is currently composed in, for tests and tooling. */
  get viewPlaneUp(): Vector3 {
    return this.viewUp;
  }

  /**
   * Swing the view from the pinned plane onto the body's plane.
   *
   * This is the gesture the head tilt and `Q` both pull: the whole frame
   * rotates (about the seam the two planes share) onto the surface the body is
   * standing on. Returns true when a swing started, so the caller can tell a
   * deliberate press from a no-op.
   */
  swivelToBodyPlane(): boolean {
    if (!this.pinnedPlane) return false;
    if (this.swivelElapsed < BALANCE.camera.swivel.duration) return false;
    this.swivelElapsed = 0;
    return true;
  }

  private releasePin(): void {
    this.pinnedPlane = false;
    this.swivelElapsed = Number.POSITIVE_INFINITY;
  }

  reset(): void {
    this.relativeYaw = 0;
    this.relativePitch = 0;
    this.lastHeading = 0;
    this.lastSurfaceId = null;
    this.lastForward.set(0, 0, -1);
    this.smoothedUp.set(0, 1, 0);
    this.viewUp.set(0, 1, 0);
    this.pinnedUp.set(0, 1, 0);
    this.leanLockYaw = 0;
    this.leanLockPitch = 0;
    this.releasePin();
    this.clearHeadTracking();
    this.thirdPerson.reset();
    this.blendElapsed = Number.POSITIVE_INFINITY;
    this.cancelLookEase();
    this.recoilPitch = 0;
    this.shakeStrength = 0;
    this.shakeTime = 0;
    this.applyFov(0);
  }

  /**
   * The up the camera is composed in.
   *
   * Normally the body's up, damped in flight so a fast mid-air reorientation
   * does not spin the world; after a sideways step it is the plane the player
   * stepped off, until they swivel onto the new one. Everywhere unpinned it is
   * the body's up exactly: the roll at a corner is deliberate.
   */
  get bodyUp(): Vector3 {
    return this.viewUp;
  }

  /** The direction the camera is looking in the world, without the head tracking offset. */
  get viewDirection(): Vector3 {
    return this.baseForward;
  }

  update(dt: number, robot: Robot, horizonLock: boolean): void {
    this.syncPlanePin(robot);
    this.syncWithBody(robot);
    this.updateLookEase(dt);
    // The turret can face the full circle in both modes.
    this.relativeYaw = wrapAngle(this.relativeYaw);
    this.updateSmoothedUp(dt, robot);
    this.updateLookFrame(dt, robot);
    this.updateViewBasis(robot);

    // While the picture is held on the plane being left, the camera is
    // composed in that plane: the roll and the look axes both come from
    // `viewUp`, so the mouse stays screen-relative on any plane.
    const cameraUp = this.pinnedPlane ? this.viewUp : this.smoothedUp;
    const planeUp = this.pinnedPlane ? this.viewUp : robot.up;

    if (this.mode === 'third') {
      const limitPitch = degToRad(BALANCE.camera.head.maxPitch);
      const viewYaw = this.relativeYaw + this.leanLockYaw;
      const viewPitch = clamp(
        this.relativePitch + this.recoilPitch + this.leanLockPitch,
        -limitPitch,
        limitPitch,
      );
      const aimForward = this.aimDirection(robot, viewYaw, viewPitch, scratchAim);
      this.thirdPerson.update(
        dt,
        robot,
        this.baseForward,
        cameraUp,
        this.targetTransform,
        viewPitch,
        aimForward,
        0,
        this.scaledViewOffset(scratchViewerOffset),
      );
    } else {
      this.firstPerson.headYaw = this.relativeYaw + this.leanLockYaw;
      this.firstPerson.headPitch = this.relativePitch + this.recoilPitch + this.leanLockPitch;
      this.firstPerson.headRoll = 0;
      this.firstPerson.viewOffset.copy(this.viewOffset);
      this.firstPerson.clampPitch();
      this.firstPerson.update(
        robot,
        this.lookReference,
        this.targetTransform,
        horizonLock,
        cameraUp,
        planeUp,
      );
    }

    // The field of view opens up while the body is turning, so a fast
    // mid-air reorientation does not feel like a jolt. A corner or landing is
    // already a large visual change, so its kick is deliberately subdued.
    const transitionKick =
      robot.isTransitioning || robot.state === 'LANDING'
        ? BALANCE.camera.transitionFovKickScale
        : 1;
    this.applyFov(
      clamp(robot.angularSpeed * BALANCE.camera.fovKickPerRadian, 0, BALANCE.camera.maxFovKick) *
        transitionKick,
    );

    // The viewer offset is a projection effect, not a camera-mode effect. With
    // no tracking the offset is zero, so this also restores the standard
    // projection after tracking is disabled.
    this.applyOffAxisProjection();

    if (this.blendElapsed < BALANCE.camera.blendDuration) {
      this.blendElapsed += dt;
      const t = easeInOutCubic(clamp(this.blendElapsed / BALANCE.camera.blendDuration, 0, 1));
      this.camera.position.lerpVectors(
        this.fromTransform.position,
        this.targetTransform.position,
        t,
      );
      this.camera.quaternion.slerpQuaternions(
        this.fromTransform.quaternion,
        this.targetTransform.quaternion,
        t,
      );
    } else {
      this.camera.position.copy(this.targetTransform.position);
      this.camera.quaternion.copy(this.targetTransform.quaternion);
    }

    if (this.shakeStrength > 0.001) {
      this.shakeTime += dt;
      const amplitude = this.shakeStrength * 0.06;
      scratchRight.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
      scratchUp.set(0, 1, 0).applyQuaternion(this.camera.quaternion);
      this.camera.position.addScaledVector(scratchRight, Math.sin(this.shakeTime * 72) * amplitude);
      this.camera.position.addScaledVector(scratchUp, Math.cos(this.shakeTime * 57) * amplitude);
      this.shakeStrength *= Math.exp(-9 * dt);
    } else {
      this.shakeStrength = 0;
    }

    // The movement basis follows the view, projected onto the current surface.
    this.updateViewBasis(robot);
  }

  /**
   * Rebuild the projection matrix as an off-axis window.
   *
   * The camera orientation stays the mouse aim, but the principal point moves
   * with the viewer so the virtual screen stays fixed in front of the aim. The
   * same window is used by both camera modes; in third person the offset is
   * scaled up to match the spring arm's translation, so the arm moves and the
   * picture gains depth instead of sliding as one piece.
   */
  private applyOffAxisProjection(): void {
    const off = BALANCE.camera.offAxis;
    const camera = this.camera;
    this.scaledViewOffset(scratchViewerOffset);
    const distance = Math.max(off.minScreenDistance, this.windowDistance - scratchViewerOffset.z);
    const halfHeight = Math.tan(degToRad(camera.fov) / 2) * this.windowDistance;
    const halfWidth = halfHeight * camera.aspect;
    const scale = camera.near / distance;

    const left = (-halfWidth - scratchViewerOffset.x) * scale;
    const right = (halfWidth - scratchViewerOffset.x) * scale;
    const top = (halfHeight - scratchViewerOffset.y) * scale;
    const bottom = (-halfHeight - scratchViewerOffset.y) * scale;

    camera.projectionMatrix.makePerspective(left, right, top, bottom, camera.near, camera.far);
    camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
  }

  /**
   * Damp the body's up while the body is turning freely in mid air.
   */
  private updateSmoothedUp(dt: number, robot: Robot): void {
    const transitioning = robot.isTransitioning || robot.state === 'LANDING';
    const rate =
      robot.state === 'FREE_FLIGHT'
        ? BALANCE.camera.upDamping
        : transitioning
          ? BALANCE.camera.transitionUpDamping
          : BALANCE.camera.surfaceUpDamping;
    const t = 1 - Math.exp(-rate * dt);
    this.smoothedUp.lerp(robot.up, t);
    if (this.smoothedUp.lengthSq() < 1e-8) this.smoothedUp.copy(robot.up);
    this.smoothedUp.normalize();
    // Once the body is settled, keep the camera exactly on its up so no
    // residual lag survives into normal play.
    if (!transitioning && this.smoothedUp.dot(robot.up) > 0.99999) {
      this.smoothedUp.copy(robot.up);
    }
  }

  /**
   * The direction the head is aiming, in world space.
   *
   * This is the aim the gun uses, and it is built from the mouse look angles
   * in both modes: the head tracker only ever moves the viewpoint, so moving
   * your face can never swing the shot. In first person the eye is the aim
   * device, so `getAimDirection` is the matching ray.
   */
  getHeadAimDirection(robot: Robot, out = new Vector3()): Vector3 {
    if (this.mode === 'first') return this.getAimDirection(out);
    const limit = degToRad(BALANCE.camera.head.maxPitch);
    const pitch = clamp(this.headAimPitch, -limit, limit);
    return this.aimDirection(robot, this.headAimYaw, pitch, out);
  }

  /**
   * The up the look angles are built in.
   *
   * The body's own up normally. While the view is pinned to the plane the
   * player stepped off it is that plane, so the aim, the steering basis and the
   * roll all turn together and the mouse stays screen-relative.
   */
  private lookAxisUp(robot: Robot): Vector3 {
    return this.pinnedPlane ? this.viewUp : robot.up;
  }

  /** Build an aim direction from a yaw and pitch in the current look frame. */
  private aimDirection(robot: Robot, yaw: number, pitch: number, out: Vector3): Vector3 {
    const axisUp = this.lookAxisUp(robot);
    rightFromForwardUp(this.lookReference, axisUp, scratchRight);
    out
      .copy(this.lookReference)
      .multiplyScalar(Math.cos(yaw))
      .addScaledVector(scratchRight, Math.sin(yaw))
      .normalize();

    rightFromForwardUp(out, axisUp, scratchRight);
    out.multiplyScalar(Math.cos(pitch)).addScaledVector(axisUp, Math.sin(pitch));
    return out.normalize();
  }

  /**
   * The gun muzzle's world position.
   *
   * The muzzle follows the head, so the weapon the player can see and the
   * weapon that fires are the same weapon — including the head's translation
   * under a tracked lean.
   */
  getMuzzlePosition(robot: Robot, out = new Vector3()): Vector3 {
    const offset = this.getHeadRigOffset(robot, scratchViewerOffset);
    headRotation(this.headAimYaw, this.headAimPitch, this.headAimRoll, scratchQuat);
    return muzzleWorldPosition(robot.position, robot.bodyQuaternion, scratchQuat, out, offset);
  }

  /**
   * The head pivot's world position, where the third-person aim ray starts.
   */
  getHeadPosition(robot: Robot, out = new Vector3()): Vector3 {
    return headWorldPosition(
      robot.position,
      robot.bodyQuaternion,
      out,
      this.getHeadRigOffset(robot, scratchViewerOffset),
    );
  }

  /**
   * The viewer offset as a translation of the visible head rig, in the body's
   * local frame (zero when the head should not move).
   *
   * In first person the eye is mounted on the head, so the head — and the gun
   * bolted to it — shift with the viewer and stay put relative to the view.
   * That is what makes the gun go where the player goes. In third person the
   * camera moves instead and the body stays as the anchor the parallax reads
   * against, so the head is left alone.
   */
  getHeadRigOffset(robot: Robot, out = new Vector3()): Vector3 {
    out.set(0, 0, 0);
    if (this.mode !== 'first' || this.viewOffset.lengthSq() < 1e-12) return out;

    scratchRigRight.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
    scratchRigUp.set(0, 1, 0).applyQuaternion(this.camera.quaternion);
    scratchRigForward.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
    out
      .addScaledVector(scratchRigRight, this.viewOffset.x)
      .addScaledVector(scratchRigUp, this.viewOffset.y)
      .addScaledVector(scratchRigForward, this.viewOffset.z);
    // The rig lives in the body frame, so undo the body's orientation.
    scratchRigInverse.copy(robot.bodyQuaternion).invert();
    return out.applyQuaternion(scratchRigInverse);
  }

  /**
   * Pin the view plane when a sideways step begins.
   *
   * A step changes the surface the body is standing on, not the picture: the
   * view is held on the plane being left until the player swivels onto the new
   * one. The robot's transition count is monotonic, so this fires exactly once
   * per step and re-pins on a second step even if the first was never settled.
   */
  private syncPlanePin(robot: Robot): void {
    if (robot.transitionCount === this.lastTransitionCount) return;
    this.lastTransitionCount = robot.transitionCount;
    if (!this.planeSwivel || !robot.sidewaysTransitions) return;

    if (this.pinnedPlane) {
      // A second step before the player has settled: keep holding the frame
      // they can actually see (which may be part-way through a swivel), and
      // wait for a fresh settle rather than jumping to the wall the body
      // passed through.
      this.pinnedUp.copy(this.viewUp).normalize();
      this.pinnedForward.copy(this.lookReference).normalize();
    } else {
      // Hold the plane the player is leaving: its up and the forward the yaw
      // was measured from. Together they are a fixed world frame, so the body's
      // blend underneath cannot drag the picture.
      this.pinnedUp.copy(this.smoothedUp);
      if (this.pinnedUp.lengthSq() < 1e-8) this.pinnedUp.copy(robot.up);
      this.pinnedUp.normalize();
      // The forward the yaw was measured from last frame, not the aim itself:
      // keeping the same reference and yaw reproduces the same picture exactly.
      this.pinnedForward.copy(this.lookReference);
      if (this.pinnedForward.lengthSq() < 1e-8) this.pinnedForward.copy(robot.forward);
      this.pinnedForward.normalize();
    }

    this.pinnedPlane = true;
    this.swivelElapsed = Number.POSITIVE_INFINITY;
  }

  /**
   * Keep the look frame current: held on the plane being left, then swung onto
   * the body's plane once the player asks for it.
   *
   * The frame is its up *and* the forward the yaw is measured from, and both
   * are blended together so the whole picture rotates as one piece — which is
   * the slow swivel the player asked for.
   */
  private updateLookFrame(dt: number, robot: Robot): void {
    if (!this.pinnedPlane) {
      this.viewUp.copy(this.smoothedUp);
      this.lookReference.copy(robot.forward);
      return;
    }
    // Free flight and a landing own the roll: re-express the look in the
    // body's frame so the aim does not snatch, then follow the body as usual.
    if (robot.state !== 'SURFACE') {
      this.expressLookInBodyFrame(robot);
      this.releasePin();
      this.viewUp.copy(this.smoothedUp);
      this.lookReference.copy(robot.forward);
      return;
    }

    const swivel = BALANCE.camera.swivel;
    if (this.swivelElapsed < swivel.duration) {
      this.swivelElapsed = Math.min(swivel.duration, this.swivelElapsed + dt);
      const t = easeInOutCubic(this.swivelElapsed / swivel.duration);
      // The swing is the shortest arc between the two planes, which is the
      // roll about the seam they share.
      this.viewUp.copy(this.pinnedUp).lerp(robot.up, t);
      this.lookReference.copy(this.pinnedForward).lerp(robot.forward, t);
      if (this.viewUp.lengthSq() < 1e-8) this.viewUp.copy(robot.up);
      if (this.lookReference.lengthSq() < 1e-8) this.lookReference.copy(robot.forward);
      this.viewUp.normalize();
      this.lookReference.normalize();
      if (this.swivelElapsed >= swivel.duration) {
        // The frame has arrived at the body's own, so the pin simply ends.
        this.releasePin();
        this.viewUp.copy(robot.up);
        this.lookReference.copy(robot.forward);
      }
      return;
    }

    this.viewUp.copy(this.pinnedUp);
    this.lookReference.copy(this.pinnedForward);
  }

  /**
   * Recompute the view basis from the current yaw and the robot's orientation.
   * Called after the look input so the movement basis never lags a frame
   * behind the mouse.
   */
  updateViewBasis(robot: Robot): void {
    // Base view direction: the look reference rotated around the plane's up.
    // While the view is pinned both are the plane being left, so the mouse
    // stays screen-relative and the aim does not move as the body blends;
    // otherwise they are the body's forward and up, exactly as before.
    const axisUp = this.lookAxisUp(robot);
    rightFromForwardUp(this.lookReference, axisUp, scratchRight);
    this.baseForward
      .copy(this.lookReference)
      .multiplyScalar(Math.cos(this.relativeYaw))
      .addScaledVector(scratchRight, Math.sin(this.relativeYaw))
      .normalize();

    this.viewForward.copy(this.baseForward);
    this.viewForward.addScaledVector(robot.up, -this.viewForward.dot(robot.up));
    if (this.viewForward.lengthSq() < 1e-6) this.viewForward.copy(robot.forward);
    this.viewForward.normalize();
    this.viewRight.copy(rightFromForwardUp(this.viewForward, robot.up, this.viewRight));
  }

  /**
   * Keep the camera's world direction steady while the body turns.
   *
   * The view yaw is stored as an offset from the body's forward, so a body
   * that turns would otherwise swing the camera with it. The body chases the
   * direction the player is steering in, which is derived from the camera,
   * so without this compensation the two chase each other and the robot
   * walks in circles the moment the player strafes.
   *
   * Compensating by the body's heading change keeps the camera's world
   * direction fixed, which gives the body a target it can actually settle on.
   *
   * A surface change is a rotation of the *whole* frame rather than a turn
   * within one, so there is nothing to compensate here: the offsets are kept
   * relative to the body, which is exactly "the same look, on the new surface".
   * That is what keeps the steering honest — `getViewForward` is built from
   * these offsets, so whatever W was doing before a corner carries on doing it
   * along the new surface. Only the roll lags (`updateSmoothedUp`), so the
   * surface change arrives as a readable turn instead of a snap.
   */
  private syncWithBody(robot: Robot): void {
    const surfaceId = robot.surface?.id ?? null;

    // Nothing to reconcile against yet (a fresh rig, or a new run), so take the
    // body's frame as the reference rather than reconciling against a stale one.
    if (this.lastSurfaceId === null) {
      this.lastSurfaceId = surfaceId;
      this.lastHeading = robot.heading;
      this.lastForward.copy(robot.forward);
      return;
    }

    if (this.pinnedPlane && robot.state === 'SURFACE') {
      // While the view is held on a plane the body is not standing in, the look
      // frame is a fixed world plane: compensating the body's turn into it
      // would rotate the picture, which is the one thing the pin exists to
      // prevent. Only the bookkeeping is kept current, so the hand-off back to
      // the body's frame is seamless.
      this.lastSurfaceId = surfaceId;
      this.lastHeading = robot.heading;
      this.lastForward.copy(robot.forward);
      return;
    }

    if (robot.state === 'FREE_FLIGHT' && !robot.isTransitioning) {
      // The body turns itself around in mid air. The camera keeps its place in
      // the world instead of orbiting with it, so the somersault reads as the
      // dog rotating under a steady view.
      const delta = signedAngleAround(this.lastForward, robot.forward, robot.up);
      if (delta !== 0) {
        this.relativeYaw -= delta;
        this.lastForward.copy(robot.forward);
      }
      this.lastSurfaceId = surfaceId;
      this.lastHeading = robot.heading;
      return;
    }

    const reanchored =
      robot.state !== 'SURFACE' || robot.isTransitioning || surfaceId !== this.lastSurfaceId;

    if (reanchored) {
      if (robot.sidewaysTransitions && robot.isTransitioning && !this.pinnedPlane) {
        // The pin off: a sideways step re-expresses the frame on the new
        // surface, so the body's turn is compensated to hold the aim. With the
        // pin on there is nothing to compensate — the look frame is a fixed
        // world plane the body blends underneath (see `updateLookFrame`).
        const delta = signedAngleAround(this.lastForward, robot.forward, robot.up);
        if (delta !== 0) this.relativeYaw -= delta;
      }
      this.lastSurfaceId = surfaceId;
      this.lastHeading = robot.heading;
      this.lastForward.copy(robot.forward);
      return;
    }

    const delta = wrapAngle(robot.heading - this.lastHeading);
    if (delta !== 0) {
      this.relativeYaw -= delta;
      this.lastHeading = robot.heading;
    }
    this.lastForward.copy(robot.forward);
  }

  /**
   * Re-express the current look yaw in the body's own frame.
   *
   * `updateViewBasis` builds the aim as `reference * cos(yaw) + right * sin(yaw)`
   * about the plane's up, so the inverse gives the yaw that points at the same
   * direction from the body's forward. Used when the pin ends for a reason
   * other than the swivel, so the aim does not snatch.
   */
  private expressLookInBodyFrame(robot: Robot): void {
    rightFromForwardUp(robot.forward, robot.up, scratchHoldRight);
    if (scratchHoldRight.lengthSq() < 1e-8) return;
    const cosine = this.baseForward.dot(robot.forward);
    const sine = this.baseForward.dot(scratchHoldRight);
    if (Math.hypot(cosine, sine) < 1e-6) return;
    this.relativeYaw = wrapAngle(Math.atan2(sine, cosine));
  }

  /** Direction the player is steering in (never includes the head-tracking offset). */
  getViewForward(out = new Vector3()): Vector3 {
    return out.copy(this.viewForward);
  }

  /** Right-hand direction of the view basis. */
  getViewRight(out = new Vector3()): Vector3 {
    return out.copy(this.viewRight);
  }

  /**
   * The basis the body steers by.
   *
   * Normally this is the view basis, so movement is screen-relative. While the
   * picture is held on the plane the player just left it is that same frame
   * carried onto the body's new plane by the swivel rotation (about the seam
   * the two planes share). So `W` keeps meaning "the way I was going": walking
   * into a wall, it steers *up* the wall rather than collapsing onto the seam,
   * and the steering already agrees with the picture the moment `Q` settles it.
   */
  getSteerBasis(robot: Robot, outForward = new Vector3(), outRight = new Vector3()): void {
    if (!this.pinnedPlane) {
      outForward.copy(this.viewForward);
      outRight.copy(this.viewRight);
      return;
    }

    const axis = scratchSteerAxis.crossVectors(this.viewUp, robot.up);
    if (axis.lengthSq() < 1e-8) {
      // Same plane: nothing to carry across.
      outForward.copy(this.viewForward);
      outRight.copy(this.viewRight);
      return;
    }
    axis.normalize();
    const angle = Math.acos(clamp(this.viewUp.dot(robot.up), -1, 1));

    outForward.copy(this.baseForward).applyAxisAngle(axis, angle);
    // Re-project: the rotation is exact, but stays clear of float drift.
    outForward.addScaledVector(robot.up, -outForward.dot(robot.up));
    if (outForward.lengthSq() < 1e-6) outForward.copy(robot.forward);
    outForward.normalize();
    rightFromForwardUp(outForward, robot.up, outRight);
    if (outRight.lengthSq() < 1e-6) outRight.copy(robot.right);
    outRight.normalize();
  }

  /** The actual direction the camera is pointing, including head tracking. */
  getAimDirection(out = new Vector3()): Vector3 {
    return this.camera.getWorldDirection(out);
  }
}
